// ═══════════════════════════════════════════════════════════════
// ROTEIRO DA ABORDAGEM — o que a conversa precisa cobrir
// ═══════════════════════════════════════════════════════════════
//
// A gerente escreve os textos explicativos: o repasse, o prazo, como funciona
// a ordem de serviço. Eles entram na análise como REFERÊNCIA DE CONTEÚDO — a
// IA confere se o assunto ficou claro, e não se as palavras bateram.
//
// A diferença não é detalhe. Texto padrão usado como script vira conversa
// mecânica: bloco grande, tom de folheto, o fornecedor não lê até o fim e não
// responde. O objetivo do roteiro é o contrário — que o vendedor SAIBA o que
// precisa dizer e diga do jeito dele.
//
// SEM IMPORTS DE PROPÓSITO: este arquivo é lido pelo navegador e pela função
// serverless que fala com a OpenAI. Qualquer import de React ou de utilitário
// do app quebraria o segundo uso.

// ── Os três eixos que separam um texto do outro ──────────────────
//
// Sem eles a IA acusa conversa certa de errada: cobra fluxo de ordem de serviço
// de um posto, mistura a taxa da Funai com a da UFRGS, e reclama de "não
// explicou taxa" numa primeira mensagem que o fornecedor nem respondeu.

export const RAMOS = [
  { id: "ambos", label: "Os dois" },
  { id: "combustivel", label: "Posto de combustível" },
  { id: "manutencao", label: "Oficina e manutenção" },
];

export const MOMENTOS = [
  { id: "sempre", label: "Sempre", dica: "Vale desde a primeira mensagem" },
  { id: "se_avancou", label: "Se a conversa avançou", dica: "Só depois que o fornecedor demonstrou interesse" },
];

/** Posto puxa o material de combustível; o resto, o de manutenção. */
export const ramoDoParceiro = (tipo) => (tipo === "posto" ? "combustivel" : "manutencao");

const serveAoRamo = (p, ramo) => !ramo || !p.ramo || p.ramo === "ambos" || p.ramo === ramo;

/** Os pontos ativos, na ordem em que a gerência colocou. */
export function pontosAtivos(roteiro = [], { ramo = null } = {}) {
  return (Array.isArray(roteiro) ? roteiro : [])
    .filter((p) => p && p.ativo !== false && String(p.titulo || "").trim() && serveAoRamo(p, ramo))
    .sort((a, b) => (Number(a.ordem) || 0) - (Number(b.ordem) || 0));
}

export const roteiroVazio = (roteiro = [], opcoes) => pontosAtivos(roteiro, opcoes).length === 0;

// ── Como a conversa deve soar ────────────────────────────────────
//
// Isto NÃO vem do banco: não é material da gerência, é a régua do sistema, e
// vale igual para todo ponto do roteiro. Ficar em código evita que alguém
// apague sem querer a única parte que impede a IA de premiar o texto colado.
export const REGRAS_HUMANIZACAO = `COMO A CONVERSA DEVE SOAR:
· Conversa de gente, não de folheto. Frase curta, tom de quem trabalha no ramo.
· Mensagem longa não é sinal de esforço — é o motivo de o fornecedor parar de
  ler no meio. Bloco enorme logo na abertura conta como ERRO, mesmo que o
  conteúdo esteja certo.
· O texto padrão da empresa é referência do QUE dizer, nunca do COMO. Vendedor
  que cola o texto pronto está fazendo o pior tipo de abordagem: parece robô,
  não abre espaço para pergunta e não se adapta ao que o fornecedor respondeu.
· Cobrir um ponto significa que o fornecedor entenderia aquilo lendo a conversa
  — pode estar dito com outras palavras, em partes, ou respondendo a uma
  pergunta dele. Palavra igual à do texto padrão não é o que se mede.
· Ponto que não cabia naquela conversa não é falha: se o assunto não veio, é
  "nao_veio", não "nao_cobriu".
· Colar na PRIMEIRA mensagem, para quem ainda não respondeu nada, é falta leve.
  Colar em RESPOSTA a uma pergunta do fornecedor é o erro grave: significa que
  ninguém leu o que ele perguntou.`;

// A regra que impede o pior erro possível deste sistema: acusar de errado o
// vendedor que acertou. Os números mudam por contrato e se contradizem entre
// contratos — sem esta trava, a IA marcaria a tabela CERTA como divergência.
export const REGRAS_PRECISAO = `SOBRE OS NÚMEROS (leia antes de apontar erro):
· Cada contrato tem condições próprias, e elas SE CONTRADIZEM entre si de
  propósito. O material abaixo está separado por contrato justamente por isso.
· Descubra de qual contrato a conversa trata — o vendedor costuma citar o órgão
  logo na abertura. Confira o que ele disse contra o material DAQUELE contrato.
· Se não der para saber qual é o contrato, NÃO acuse divergência. Registre nos
  alertas que não deu para confirmar de qual contrato se trata.
· Só aponte erro quando o valor dito contradiz o material do MESMO contrato.
  Número que bate com outro contrato da lista não é erro do vendedor: é sinal
  de que você identificou o contrato errado.
· Nunca corrija de cabeça. Se o número não estiver no material, ele não é seu
  para julgar.

QUAIS PONTOS AVALIAR:
· Só os do contrato que a conversa trata, mais os de "Vale para qualquer
  contrato". Contratos são ALTERNATIVOS, não somados: os pontos dos outros
  contratos são "nao_veio", nunca "nao_cobriu". Ninguém fala da Funai e da
  UFRGS na mesma conversa.
· Ponto marcado "(só se a conversa avançou)" só é cobrado depois que o
  fornecedor demonstrou interesse. Numa primeira mensagem sem resposta, ele é
  "nao_veio" — cobrar taxa e faturamento de quem nem respondeu ainda é
  cobrança injusta, e parecer injusto é parecer que ninguém lê.`;

const CONTEXTO_ROTULO = {
  geral: "Vale para qualquer contrato",
  "orgao-publico": "Órgãos públicos em geral",
  "empresa-privada": "Empresas privadas",
  prefeituras: "Prefeituras",
};

const rotularContexto = (c) => CONTEXTO_ROTULO[c] || `Contrato: ${String(c || "geral").toUpperCase()}`;

/**
 * O bloco de referência que vai dentro do prompt, separado por contrato.
 * Vazio quando não há roteiro que sirva a este ramo.
 */
export function paraPrompt(roteiro = [], { ramo = null } = {}) {
  const pontos = pontosAtivos(roteiro, { ramo });
  if (!pontos.length) return "";

  const linhas = ["", "O QUE ESTA CONVERSA PRECISAVA COBRIR (material da gerência):"];
  if (ramo) {
    linhas.push(ramo === "combustivel"
      ? "Este fornecedor é um POSTO DE COMBUSTÍVEL — só o material de abastecimento se aplica."
      : "Este fornecedor é OFICINA/MANUTENÇÃO — só o material de manutenção se aplica.");
  }

  // Agrupado por contrato: é o que permite ao modelo comparar o número dito
  // com a tabela certa em vez de com a média de todas.
  const porContexto = new Map();
  for (const p of pontos) {
    const c = p.contexto || "geral";
    if (!porContexto.has(c)) porContexto.set(c, []);
    porContexto.get(c).push(p);
  }
  // "geral" primeiro: é o que vale sempre, e o resto são exceções por contrato.
  const ordem = [...porContexto.keys()].sort((a, b) => (a === "geral" ? -1 : b === "geral" ? 1 : a.localeCompare(b)));

  for (const c of ordem) {
    linhas.push("");
    linhas.push(`── ${rotularContexto(c)} ──`);
    for (const p of porContexto.get(c)) {
      const so = p.quando === "se_avancou" ? " (só se a conversa avançou)" : "";
      linhas.push(`· [${p.id}] ${p.titulo}${so}`);
      if (String(p.objetivo || "").trim()) linhas.push(`  Precisa ficar claro: ${p.objetivo.trim()}`);
      const t = String(p.textoPadrao || p.texto_padrao || "").trim().replace(/\s+/g, " ");
      // Truncado: o modelo precisa do assunto e dos números, não do texto
      // inteiro decorado — e cada palavra aqui é token pago em TODA análise.
      if (t) linhas.push(`  Referência: "${t.slice(0, 700)}${t.length > 700 ? "…" : ""}"`);
    }
  }

  linhas.push("");
  linhas.push(REGRAS_PRECISAO);
  linhas.push("");
  linhas.push(REGRAS_HUMANIZACAO);
  return linhas.join("\n");
}

// ── Colagem de texto padrão: conferência determinística ──────────
//
// Aqui NÃO se pergunta ao modelo. Trecho literal é comparação de string, sai
// de graça e não erra — e o modelo, sabendo que o texto padrão existe, tende a
// achar semelhança onde há só assunto em comum.

const limpar = (s) => String(s || "")
  .toLowerCase()
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9\s]/g, " ")
  .replace(/\s+/g, " ")
  .trim();

const JANELA = 8; // palavras seguidas iguais: abaixo disso é coincidência de ramo

/**
 * Devolve os pontos cujo texto padrão apareceu literalmente nas mensagens da
 * equipe. Oito palavras seguidas idênticas não acontecem por acaso.
 */
export function trechosColados(mensagens = [], roteiro = [], opcoes = {}) {
  const daEquipe = (Array.isArray(mensagens) ? mensagens : [])
    .filter((m) => m && m.de === "equipe")
    .map((m) => limpar(m.texto));
  if (!daEquipe.length) return [];

  const achados = [];
  for (const p of pontosAtivos(roteiro, opcoes)) {
    const palavras = limpar(p.textoPadrao || p.texto_padrao).split(" ").filter(Boolean);
    if (palavras.length < JANELA) continue;
    for (let i = 0; i + JANELA <= palavras.length; i++) {
      const trecho = palavras.slice(i, i + JANELA).join(" ");
      if (daEquipe.some((t) => t.includes(trecho))) {
        achados.push({ id: p.id, titulo: p.titulo, trecho });
        break;
      }
    }
  }
  return achados;
}

/** Mensagens da equipe grandes demais para alguém ler no WhatsApp. */
export const LIMITE_MENSAGEM = 450;

export function mensagensLongas(mensagens = [], limite = LIMITE_MENSAGEM) {
  return (Array.isArray(mensagens) ? mensagens : [])
    .filter((m) => m && m.de === "equipe" && String(m.texto || "").length > limite)
    .map((m) => ({ hora: m.hora || "", tamanho: String(m.texto || "").length }));
}
